import { useState, useEffect, useRef } from 'react';
import {
  Text,
  Button,
  BlockStack,
  InlineStack,
  List,
  Modal,
  Badge,
  Collapsible,
  Divider,
  Icon,
} from '@shopify/polaris';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlayIcon,
  BookIcon,
  SettingsIcon,
  ChartVerticalFilledIcon,
  FileIcon,
  QuestionCircleIcon,
  BugIcon,
  MenuHorizontalIcon,
  ClipboardIcon,
  CheckIcon,
  EmailIcon,
} from '@shopify/polaris-icons';
import { Layout } from '../components/Layout';
import {
  HelpSidebar,
  HelpSearch,
  HelpCategoryCards,
  HelpSection,
  StepItem,
  FAQList,
  type HelpCategory,
  type FAQ,
} from '../components/help';

// Category definitions
const HELP_CATEGORIES: (HelpCategory & { description: string })[] = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: BookIcon,
    color: '#2c6ecb',
    description: 'Login options and site setup',
  },
  {
    id: 'setup-ga4',
    label: 'Setting Up GA4',
    icon: SettingsIcon,
    color: '#667eea',
    description: 'Connect Google Analytics',
  },
  {
    id: 'reports',
    label: 'Understanding Reports',
    icon: ChartVerticalFilledIcon,
    color: '#008060',
    description: 'Learn about report types',
  },
  {
    id: 'site-management',
    label: 'Site Management',
    icon: FileIcon,
    color: '#f4a21b',
    description: 'Manage sites and settings',
  },
  {
    id: 'faqs',
    label: 'FAQs',
    icon: QuestionCircleIcon,
    color: '#9c6ade',
    description: 'Common questions answered',
  },
  {
    id: 'troubleshooting',
    label: 'Troubleshooting',
    icon: BugIcon,
    color: '#bf0711',
    description: 'Solve common issues',
  },
];

// FAQ categories for grouping
const FAQ_CATEGORIES = [
  { id: 'setup', title: 'Setup & Configuration', icon: SettingsIcon, color: '#667eea' },
  { id: 'reports', title: 'Reports & Analytics', icon: ChartVerticalFilledIcon, color: '#008060' },
  { id: 'ecommerce', title: 'E-commerce & Data', icon: FileIcon, color: '#f4a21b' },
  { id: 'management', title: 'Site Management', icon: BugIcon, color: '#bf0711' },
];

// Reorganized FAQs with categories
const FAQS: FAQ[] = [
  {
    id: 'faq-1',
    category: 'setup',
    question: 'How do I connect my Google Analytics account?',
    answer:
      'Go to your site settings page → Click "Connect Google Account" → Authorize Analyzely in the Google popup → Then follow the setup wizard to select your GA4 account, property, and data stream.',
  },
  {
    id: 'faq-2',
    category: 'setup',
    question: 'How do I apply the tracking code to my Webflow site?',
    answer:
      'After configuring your GA4 property in Analyzely, click "Apply Tracking Code" in your site settings. This will inject the GA4 tracking script into your Webflow site\'s header. Important: You must publish your Webflow site after applying the code for changes to take effect.',
  },
  {
    id: 'faq-3',
    category: 'setup',
    question: 'How do I change my GA4 property?',
    answer:
      'Go to your site settings → Click "Change Property" button → This will take you through the setup wizard again where you can select a different GA4 account, property, or data stream.',
  },
  {
    id: 'faq-4',
    category: 'reports',
    question: 'What date ranges are available for reports?',
    answer:
      'You can choose from: Last 7 days, Last 15 days, Last 30 days, or a Custom date range. Click the date buttons above your reports to change the time period. Real-time reports always show live data.',
  },
  {
    id: 'faq-5',
    category: 'reports',
    question: 'How do I compare data to previous periods?',
    answer:
      'In the Standard Reports tab, check the "Compare to previous period" checkbox. All metrics will then show percentage changes (up or down arrows with percentages) comparing the current period to the same length period before it.',
  },
  {
    id: 'faq-6',
    category: 'reports',
    question: 'How do I download my reports as PDF?',
    answer:
      'On the Standard Reports tab, click the "Download as PDF" button in the top right corner. This will generate a PDF document containing all your report data that you can save or share.',
  },
  {
    id: 'faq-7',
    category: 'reports',
    question: 'How often does real-time data refresh?',
    answer:
      'Real-time reports refresh automatically every 30 seconds. You\'ll see a "LIVE" indicator and the last update timestamp on the Real-time tab.',
  },
  {
    id: 'faq-8',
    category: 'ecommerce',
    question: 'Why is the E-commerce tab not visible in my reports?',
    answer:
      'The E-commerce reports tab only appears when e-commerce events are enabled for your site. Go to your site settings, expand the "E-commerce Event Tracking" section, enable the events you want to track (like add to cart, checkout, or purchase), and click "Save Changes". Once enabled, the E-commerce tab will appear in your reports.',
  },
  {
    id: 'faq-9',
    category: 'ecommerce',
    question: 'What is a Measurement ID?',
    answer:
      'A Measurement ID (format: G-XXXXXXXXXX) is a unique identifier for your GA4 web data stream. It\'s used by the tracking code to send analytics data to the correct GA4 property. You can find it in your GA4 Admin → Data Streams → Web stream details.',
  },
  {
    id: 'faq-10',
    category: 'management',
    question: 'How do I remove the tracking code from my site?',
    answer:
      'Go to your site settings → Click "Remove Tracking Code" button → Then publish your Webflow site for the changes to take effect. The GA4 tracking will stop collecting data after removal.',
  },
  {
    id: 'faq-11',
    category: 'management',
    question: 'What do the site status badges mean?',
    answer:
      '"Setup Required" (orange) = GA4 is not configured yet. "Reconnect Required" (yellow) = GA4 was configured but Google access needs to be reconnected. "Connected" (green) = Everything is set up and ready to view reports.',
  },
  {
    id: 'faq-12',
    category: 'management',
    question: 'Why is my data not showing in reports?',
    answer:
      'Data can take 24-48 hours to appear in Google Analytics after you first set up tracking. Make sure you\'ve applied the tracking code and published your Webflow site. Use the "Check for Custom Code" button in your site settings to verify the installation is successful.',
  },
];

export function Help() {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [openReportSection, setOpenReportSection] = useState<string | null>('standard');
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const isInitialMount = useRef(true);
  const isUserClick = useRef(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('care@analyzely.app');
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = 'care@analyzely.app';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    }
  };

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Scroll spy - update active category based on scroll position
  useEffect(() => {
    const sectionIds = HELP_CATEGORIES.map((cat) => cat.id);

    // Handle scroll to detect which section is in view
    const handleScroll = () => {
      // Check if user is at the bottom of page
      const isAtBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 100;
      if (isAtBottom) {
        // Highlight the last section when at bottom
        setActiveCategory('troubleshooting');
        return;
      }

      // Find the section that is currently in view
      let currentSection = sectionIds[0];
      for (const id of sectionIds) {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Section is considered active if its top is above 40% of viewport height
          if (rect.top <= window.innerHeight * 0.4) {
            currentSection = id;
          }
        }
      }
      setActiveCategory(currentSection);
    };

    // Debounce scroll handler for performance
    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedHandleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 50);
    };

    window.addEventListener('scroll', debouncedHandleScroll);
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
      clearTimeout(timeoutId);
    };
  }, [searchQuery]); // Re-run when search changes

  // Scroll to section when user clicks (skip on initial mount and scroll spy updates)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Only scroll if this was a user click, not a scroll spy update
    if (isUserClick.current && activeCategory && !searchQuery) {
      const element = document.getElementById(activeCategory);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      isUserClick.current = false;
    }
  }, [activeCategory, searchQuery]);

  const toggleReportSection = (section: string) => {
    setOpenReportSection(openReportSection === section ? null : section);
  };

  const handleCategoryClick = (categoryId: string) => {
    isUserClick.current = true;
    setActiveCategory(categoryId);
    setSearchQuery('');
  };

  // Filter all content based on search
  const isSearching = searchQuery.length > 0;
  const filteredFaqs = FAQS.filter(
    (faq) =>
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div style={{ display: 'flex', gap: '24px', position: 'relative' }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <button
            onClick={() => setMobileSidebarOpen(true)}
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#2c6ecb',
              color: 'white',
              border: 'none',
              boxShadow: '0 4px 12px rgba(44, 110, 203, 0.4)',
              cursor: 'pointer',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Open help menu"
          >
            <Icon source={MenuHorizontalIcon} />
          </button>
        )}

        {/* Main Content */}
        <main style={{ flex: 1, minWidth: 0 }}>
          {/* Page Header */}
          <div style={{ marginBottom: '24px' }}>
            <Text as="h1" variant="headingLg" fontWeight="bold">
              Help Center
            </Text>
            <div style={{ marginTop: '8px' }}>
              <Text as="p" tone="subdued">
                Learn how to use Analyzely to connect Google Analytics 4 to your Webflow sites
              </Text>
            </div>
          </div>

          {/* Search Bar */}
          <HelpSearch query={searchQuery} onChange={setSearchQuery} />

          {/* Category Cards - Only show when not searching */}
          {!isSearching && (
            <HelpCategoryCards categories={HELP_CATEGORIES} onCategoryClick={handleCategoryClick} />
          )}

          {/* Search Results */}
          {isSearching && (
            <div style={{ marginBottom: '24px' }}>
              <Text as="p" tone="subdued">
                Found {filteredFaqs.length} results for "{searchQuery}"
              </Text>
              <div style={{ marginTop: '16px' }}>
                <FAQList faqs={filteredFaqs} searchQuery={searchQuery} categories={FAQ_CATEGORIES} />
              </div>
              {filteredFaqs.length === 0 && (
                <div
                  style={{
                    padding: '48px',
                    textAlign: 'center',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e1e3e5',
                  }}
                >
                  <Text as="p" tone="subdued">
                    No results found. Try a different search term or browse the categories.
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* Content Sections - Only show when not searching */}
          {!isSearching && (
            <>
              {/* Getting Started Section */}
              <HelpSection
                id="getting-started"
                title="Getting Started"
                icon={BookIcon}
                color="#2c6ecb"
              >
                <BlockStack gap="400">
                  {/* Login Options */}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      Login Options
                    </Text>
                    <Text as="p" tone="subdued">
                      You can log in to Analyzely using two methods:
                    </Text>
                    <List>
                      <List.Item>
                        <Text as="span" fontWeight="semibold">
                          Webflow OAuth
                        </Text>{' '}
                        - Click "Continue with Webflow" to log in directly with your Webflow account
                        (recommended)
                      </List.Item>
                      <List.Item>
                        <Text as="span" fontWeight="semibold">
                          Magic Link
                        </Text>{' '}
                        - Enter your email address to receive a secure login link (valid for 15
                        minutes)
                      </List.Item>
                    </List>
                  </BlockStack>

                  <Divider />

                  {/* Syncing Sites */}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      Adding Your Webflow Sites
                    </Text>
                    <Text as="p" tone="subdued">
                      After logging in, click the "Add Site" button on your Dashboard to import your
                      Webflow sites. Any new sites you create in Webflow can be imported by clicking
                      this button again.
                    </Text>
                  </BlockStack>

                  <Divider />

                  {/* Site Status */}
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      Understanding Site Status
                    </Text>
                    <Text as="p" tone="subdued">
                      Each site on your Dashboard shows a status badge:
                    </Text>
                    <BlockStack gap="200">
                      <InlineStack gap="300" blockAlign="center">
                        <Badge tone="warning">Setup Required</Badge>
                        <Text as="span" tone="subdued">
                          GA4 is not configured - click to start setup
                        </Text>
                      </InlineStack>
                      <InlineStack gap="300" blockAlign="center">
                        <Badge tone="attention">Reconnect Required</Badge>
                        <Text as="span" tone="subdued">
                          Google access expired - reconnect your account
                        </Text>
                      </InlineStack>
                      <InlineStack gap="300" blockAlign="center">
                        <Badge tone="success">Connected</Badge>
                        <Text as="span" tone="subdued">
                          Ready to view analytics reports
                        </Text>
                      </InlineStack>
                    </BlockStack>
                  </BlockStack>
                </BlockStack>
              </HelpSection>

              {/* Setup GA4 Section */}
              <HelpSection
                id="setup-ga4"
                title="Setting Up GA4 Analytics"
                icon={SettingsIcon}
                color="#667eea"
              >
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="p" tone="subdued">
                      Follow these steps to connect Google Analytics 4 to your Webflow site:
                    </Text>
                    <Button onClick={() => setVideoModalOpen(true)} icon={PlayIcon}>
                      Video Tutorial
                    </Button>
                  </InlineStack>

                  <BlockStack gap="300">
                    <StepItem
                      number={1}
                      title="Connect Google Account"
                      description={`From your site's settings page, click "Connect Google Account" and authorize Analyzely to access your GA4 data in the popup window.`}
                      color="#667eea"
                    />
                    <StepItem
                      number={2}
                      title="Select GA4 Account"
                      description={`Choose the Google Analytics account that contains your property. If you don't have a GA4 account yet, create one at analytics.google.com first.`}
                      color="#667eea"
                    />
                    <StepItem
                      number={3}
                      title="Select or Create Property"
                      description={`Select your GA4 property from the dropdown. If you don't have one, click "Create New Property" and Analyzely will create it for you automatically.`}
                      color="#667eea"
                    />
                    <StepItem
                      number={4}
                      title="Select Data Stream"
                      description="Choose the web data stream for your website. The Measurement ID (G-XXXXXXXXXX) will be displayed - this is the unique identifier used for tracking."
                      color="#667eea"
                    />
                    <StepItem
                      number={5}
                      title="Apply Tracking Code"
                      description={`Click "Apply Tracking Code" to inject the GA4 tracking script into your Webflow site's header. This happens automatically through Webflow's API.`}
                      color="#667eea"
                    />
                    <StepItem
                      number={6}
                      title="Publish & Verify"
                      description={`Publish your Webflow site for the tracking code to go live. Then use "Check for Custom Code" to verify the installation is successful. Data will start appearing in reports within 24-48 hours.`}
                      color="#667eea"
                    />
                  </BlockStack>
                </BlockStack>
              </HelpSection>

              {/* Understanding Reports Section */}
              <HelpSection
                id="reports"
                title="Understanding Your Reports"
                icon={ChartVerticalFilledIcon}
                color="#008060"
              >
                <BlockStack gap="400">
                  <Text as="p" tone="subdued">
                    Analyzely provides 6 different report types to help you understand your website
                    traffic. Click each section below to learn more.
                  </Text>

                  {/* Standard Reports */}
                  <div
                    style={{
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={() => toggleReportSection('standard')}
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor:
                          openReportSection === 'standard' ? '#f6f6f7' : 'white',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#2c6ecb',
                          }}
                        />
                        <Text as="span" variant="headingSm" fontWeight="semibold">
                          Standard Reports
                        </Text>
                        <Icon
                          source={
                            openReportSection === 'standard'
                              ? ChevronDownIcon
                              : ChevronRightIcon
                          }
                        />
                      </div>
                    </button>
                    <Collapsible open={openReportSection === 'standard'} id="standard-reports">
                      <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                        <BlockStack gap="300">
                          <Text as="p" tone="subdued">
                            Core website metrics showing overall traffic and engagement:
                          </Text>
                          <List>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Sessions
                              </Text>{' '}
                              - Total number of visits to your site
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Users
                              </Text>{' '}
                              - Unique visitors to your site
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Page Views
                              </Text>{' '}
                              - Total pages viewed across all sessions
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Bounce Rate
                              </Text>{' '}
                              - Percentage of single-page visits (lower is better)
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Avg. Session Duration
                              </Text>{' '}
                              - Average time visitors spend on your site
                            </List.Item>
                          </List>
                        </BlockStack>
                      </div>
                    </Collapsible>
                  </div>

                  {/* E-commerce Reports */}
                  <div
                    style={{
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={() => toggleReportSection('ecommerce')}
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor:
                          openReportSection === 'ecommerce' ? '#f6f6f7' : 'white',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#008060',
                          }}
                        />
                        <Text as="span" variant="headingSm" fontWeight="semibold">
                          E-commerce Reports
                        </Text>
                        <Badge tone="info">Requires Setup</Badge>
                        <Icon
                          source={
                            openReportSection === 'ecommerce'
                              ? ChevronDownIcon
                              : ChevronRightIcon
                          }
                        />
                      </div>
                    </button>
                    <Collapsible open={openReportSection === 'ecommerce'} id="ecommerce-reports">
                      <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                        <BlockStack gap="300">
                          <Text as="p" tone="subdued">
                            Sales and revenue metrics (available when e-commerce events are
                            enabled):
                          </Text>
                          <List>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Total Revenue
                              </Text>{' '}
                              - Total sales amount in your currency
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Transactions
                              </Text>{' '}
                              - Number of completed purchases
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Average Order Value
                              </Text>{' '}
                              - Revenue divided by transactions
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Conversion Rate
                              </Text>{' '}
                              - Percentage of sessions that result in a purchase
                            </List.Item>
                          </List>
                        </BlockStack>
                      </div>
                    </Collapsible>
                  </div>

                  {/* Real-time Reports */}
                  <div
                    style={{
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={() => toggleReportSection('realtime')}
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor:
                          openReportSection === 'realtime' ? '#f6f6f7' : 'white',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#bf0711',
                          }}
                        />
                        <Text as="span" variant="headingSm" fontWeight="semibold">
                          Real-time Reports
                        </Text>
                        <Badge tone="success">LIVE</Badge>
                        <Icon
                          source={
                            openReportSection === 'realtime'
                              ? ChevronDownIcon
                              : ChevronRightIcon
                          }
                        />
                      </div>
                    </button>
                    <Collapsible open={openReportSection === 'realtime'} id="realtime-reports">
                      <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                        <BlockStack gap="300">
                          <Text as="p" tone="subdued">
                            Live data that refreshes automatically every 30 seconds:
                          </Text>
                          <List>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Active Users
                              </Text>{' '}
                              - Visitors currently on your site
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Live Page Views
                              </Text>{' '}
                              - Pages being viewed right now
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Top Pages (Live)
                              </Text>{' '}
                              - Most active pages with viewer count
                            </List.Item>
                          </List>
                        </BlockStack>
                      </div>
                    </Collapsible>
                  </div>

                  {/* Traffic Sources */}
                  <div
                    style={{
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={() => toggleReportSection('traffic')}
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor:
                          openReportSection === 'traffic' ? '#f6f6f7' : 'white',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#9c6ade',
                          }}
                        />
                        <Text as="span" variant="headingSm" fontWeight="semibold">
                          Traffic Sources
                        </Text>
                        <Icon
                          source={
                            openReportSection === 'traffic'
                              ? ChevronDownIcon
                              : ChevronRightIcon
                          }
                        />
                      </div>
                    </button>
                    <Collapsible open={openReportSection === 'traffic'} id="traffic-reports">
                      <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                        <BlockStack gap="300">
                          <Text as="p" tone="subdued">
                            Understand where your traffic comes from:
                          </Text>
                          <List>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Top Sources
                              </Text>{' '}
                              - Websites sending you traffic (Google, Facebook, etc.)
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Traffic by Medium
                              </Text>{' '}
                              - Organic, Referral, CPC, Social, etc.
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                UTM Campaigns
                              </Text>{' '}
                              - Track your marketing campaigns with UTM parameters
                            </List.Item>
                          </List>
                        </BlockStack>
                      </div>
                    </Collapsible>
                  </div>

                  {/* Hourly Analysis */}
                  <div
                    style={{
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={() => toggleReportSection('hourly')}
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor:
                          openReportSection === 'hourly' ? '#f6f6f7' : 'white',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#ffc453',
                          }}
                        />
                        <Text as="span" variant="headingSm" fontWeight="semibold">
                          Hourly Analysis
                        </Text>
                        <Icon
                          source={
                            openReportSection === 'hourly'
                              ? ChevronDownIcon
                              : ChevronRightIcon
                          }
                        />
                      </div>
                    </button>
                    <Collapsible open={openReportSection === 'hourly'} id="hourly-reports">
                      <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                        <BlockStack gap="300">
                          <Text as="p" tone="subdued">
                            Discover when your visitors are most active:
                          </Text>
                          <List>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Traffic Heatmap
                              </Text>{' '}
                              - Visual grid showing traffic by day and hour
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Peak Hour
                              </Text>{' '}
                              - The hour with the most traffic
                            </List.Item>
                            <List.Item>
                              <Text as="span" fontWeight="semibold">
                                Busiest Day
                              </Text>{' '}
                              - The day with the most total sessions
                            </List.Item>
                          </List>
                        </BlockStack>
                      </div>
                    </Collapsible>
                  </div>

                  <Divider />

                  {/* Report Features */}
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      Report Features
                    </Text>
                    <List>
                      <List.Item>
                        <Text as="span" fontWeight="semibold">
                          Date Range Selection
                        </Text>{' '}
                        - Choose Last 7, 15, 30 days or a custom range
                      </List.Item>
                      <List.Item>
                        <Text as="span" fontWeight="semibold">
                          Compare Periods
                        </Text>{' '}
                        - Enable comparison to see percentage changes
                      </List.Item>
                      <List.Item>
                        <Text as="span" fontWeight="semibold">
                          Download PDF
                        </Text>{' '}
                        - Export your reports as PDF documents
                      </List.Item>
                    </List>
                  </BlockStack>
                </BlockStack>
              </HelpSection>

              {/* Site Management Section */}
              <HelpSection
                id="site-management"
                title="Site Management"
                icon={FileIcon}
                color="#f4a21b"
              >
                <BlockStack gap="400">
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      Adding New Sites
                    </Text>
                    <Text as="p" tone="subdued">
                      Click the "Add Site" button on your Dashboard to import new Webflow sites.
                      This will sync all sites from your connected Webflow account. Use the
                      "Refresh" button to update the site list if you've made changes in Webflow.
                    </Text>
                  </BlockStack>

                  <Divider />

                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      Site Settings
                    </Text>
                    <Text as="p" tone="subdued">
                      Click the settings icon on any site card to access:
                    </Text>
                    <List>
                      <List.Item>Google Analytics connection status and options</List.Item>
                      <List.Item>GA4 Property ID and Measurement ID</List.Item>
                      <List.Item>Tracking code status (Applied/Not Applied)</List.Item>
                      <List.Item>E-commerce event configuration</List.Item>
                      <List.Item>Quick links to preview site or open in Webflow</List.Item>
                    </List>
                  </BlockStack>

                  <Divider />

                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      E-commerce Tracking
                    </Text>
                    <Text as="p" tone="subdued">
                      To enable e-commerce reports, go to your site settings and expand the
                      "E-commerce Event Tracking" section. You can enable individual events:
                    </Text>
                    <List>
                      <List.Item>
                        <Text as="span" fontWeight="semibold">
                          Client-side events
                        </Text>{' '}
                        - Product views, add to cart, begin checkout (tracked via JavaScript)
                      </List.Item>
                      <List.Item>
                        <Text as="span" fontWeight="semibold">
                          Server-side events
                        </Text>{' '}
                        - Purchase events (tracked via Webflow webhooks)
                      </List.Item>
                    </List>
                    <Text as="p" tone="subdued">
                      After enabling the events you want, click "Save Changes" and the E-commerce
                      tab will appear in your reports.
                    </Text>
                  </BlockStack>
                </BlockStack>
              </HelpSection>

              {/* FAQs Section */}
              <HelpSection
                id="faqs"
                title="Frequently Asked Questions"
                icon={QuestionCircleIcon}
                color="#9c6ade"
              >
                <FAQList faqs={FAQS} searchQuery="" categories={FAQ_CATEGORIES} />
              </HelpSection>

              {/* Troubleshooting Section */}
              <HelpSection
                id="troubleshooting"
                title="Need More Help?"
                icon={BugIcon}
                color="#bf0711"
              >
                <BlockStack gap="300">
                  <Text as="p" tone="subdued">
                    If you're still having issues or have questions not covered here, feel free to
                    reach out to our support team. We're here to help!
                  </Text>
                  <InlineStack gap="200">
                    <Button variant="primary" url="mailto:care@analyzely.app" external>
                      Email Support
                    </Button>
                    <Button url="https://analyzely.app" external>
                      Visit Website
                    </Button>
                  </InlineStack>

                  {/* Email with copy option for users without email client */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      backgroundColor: '#f6f6f7',
                      borderRadius: '8px',
                      marginTop: '8px',
                    }}
                  >
                    <div style={{ color: '#637381' }}>
                      <Icon source={EmailIcon} />
                    </div>
                    <Text as="span" tone="subdued">
                      care@analyzely.app
                    </Text>
                    <button
                      onClick={handleCopyEmail}
                      style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: emailCopied ? '#e4f3e5' : 'white',
                        border: `1px solid ${emailCopied ? '#008060' : '#e1e3e5'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: emailCopied ? '#008060' : '#637381',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <Icon source={emailCopied ? CheckIcon : ClipboardIcon} />
                      {emailCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </BlockStack>
              </HelpSection>
            </>
          )}
        </main>

        {/* Sidebar - Right Side */}
        <HelpSidebar
          categories={HELP_CATEGORIES}
          activeCategory={activeCategory}
          onSelect={handleCategoryClick}
          isMobile={isMobile}
          isOpen={isMobile ? mobileSidebarOpen : true}
          onClose={() => setMobileSidebarOpen(false)}
        />
      </div>

      {/* Video Tutorial Modal */}
      <Modal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        title="How to Set Up GA4 with Analyzely"
        size="large"
      >
        <Modal.Section>
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
            <iframe
              src="https://www.youtube.com/embed/jQsnPODyM3I"
              title="How to get Measurement ID"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </Modal.Section>
      </Modal>
    </Layout>
  );
}
